import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useColorMode, Box } from "@chakra-ui/react";
import SafeContent from "../Securite/safeContent";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaFilePdf, FaMusic, FaFileDownload, FaEye, FaPlus, FaTrash, FaFolderOpen } from "react-icons/fa";
import { motion } from "framer-motion";

const RessourcesList = () => {
  const { colorMode } = useColorMode();
  const [ressources, setRessources] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const isDark = colorMode === "dark";

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const fetchRessources = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ressources/recuperer`);
      setRessources(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Erreur récupération:", error);
      toast.error("Erreur de connexion au serveur.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRessources();
  }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Supprimer "${title}" ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/ressources/${id}`);
      toast.success("Supprimé !");
      fetchRessources();
    } catch (error) {
      toast.error("Erreur lors de la suppression.");
    }
  };

  const isLoggedIn = !!localStorage.getItem("token");

  const renderResourceContent = (ressource) => {
    if (!ressource) return null;

    const formatPath = (path) => {
      if (!path) return "";
      return path.startsWith('/') ? path : `/${path}`;
    };
    
    const fileType = ressource.fileType?.toLowerCase() || "";
    const isPDF = fileType.includes("pdf");
    const isAudio = fileType.includes("audio");

    return (
      <div className="flex flex-col h-full">
        {/* Container Image/Icône */}
        <div className="relative p-3">
          <div className={`overflow-hidden rounded-2xl h-48 relative ${isDark ? "bg-gray-700" : "bg-gray-100"}`}>
            {ressource.imagePath ? (
              <img
                src={`${API_URL}${formatPath(ressource.imagePath)}`}
                alt={ressource.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                {isPDF ? (
                  <FaFilePdf size={50} className="text-red-500/80" />
                ) : (
                  <FaMusic size={50} className="text-blue-600/80" />
                )}
              </div>
            )}
            {/* Badge Type de fichier (Glassmorphism) */}
            <div className="absolute top-3 right-3 bg-white/20 backdrop-blur-md border border-white/30 text-white text-[10px] px-3 py-1 rounded-full uppercase font-black tracking-widest">
              {fileType.split('/')[1] || 'DOC'}
            </div>
          </div>
        </div>

        {/* Contenu Texte */}
        <div className="px-6 pb-6 flex flex-col flex-1">
          <div className="mb-6 flex-1">
            <h2 className={`text-xl font-black mb-3 line-clamp-2 leading-tight ${isDark ? "text-white" : "text-[#1B365D]"}`}>
               {ressource.title || "Sans titre"}
            </h2>
            <div className={`text-sm font-light leading-relaxed opacity-70 line-clamp-3 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
               <SafeContent htmlContent={ressource.description || "..."} />
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 mt-auto pt-5 border-t border-gray-100 dark:border-gray-700">
            {isPDF ? (
              <>
                <a href={`${API_URL}${formatPath(ressource.filePath)}`} target="_blank" rel="noreferrer" 
                   className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-[10px] font-black uppercase tracking-tighter transition-all ${isDark ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-100 text-[#1B365D] hover:bg-gray-200"}`}>
                  <FaEye /> Consulter
                </a>
                <a href={`${API_URL}${formatPath(ressource.filePath)}`} download 
                   className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-blue-600 text-white text-[10px] font-black uppercase tracking-tighter rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition-all">
                  <FaFileDownload /> Télécharger
                </a>
              </>
            ) : isAudio ? (
              <div className="w-full bg-blue-50 dark:bg-white/5 p-2 rounded-xl">
                <audio controls className="w-full h-8">
                  <source src={`${API_URL}${formatPath(ressource.filePath)}`} type={ressource.fileType} />
                </audio>
              </div>
            ) : (
              <a href={`${API_URL}${formatPath(ressource.filePath)}`} download 
                 className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-blue-600 text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-blue-700 transition-all">
                <FaFileDownload /> Télécharger
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) return (
    <div className="flex flex-col justify-center items-center h-screen space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      <p className="text-xs font-black uppercase tracking-widest opacity-50">Chargement de la bibliothèque</p>
    </div>
  );

  return (
    <Box className={isDark ? "bg-[#0a0f16] min-h-screen" : "bg-[#FAFCFF] min-h-screen"}>
      <Helmet>
        <title>Bibliothèque de ressources — Hetep Iaout Services</title>
        <meta name="description" content="Téléchargez gratuitement nos guides et ressources sur l'aide à domicile, les aides financières et l'accompagnement des personnes dépendantes ou handicapées." />
        <link rel="canonical" href="https://www.hisvie.com/NosRessources/" />
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-6 pt-32 pb-20">
        
        {/* HEADER DA STYLE */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8">
          <div className="max-w-2xl">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border mb-6 text-[10px] font-bold uppercase tracking-widest ${isDark ? "bg-blue-900/30 border-blue-800 text-blue-300" : "bg-blue-50 border-blue-100 text-blue-800"}`}>
              <FaFolderOpen /> Documentation
            </div>
            <h1 className={`text-4xl md:text-6xl font-black tracking-tighter leading-tight ${isDark ? "text-white" : "text-[#1B365D]"}`}>
              Bibliothèque de <br />
              <span className="text-blue-600 font-serif italic font-light lowercase">Ressources.</span>
            </h1>
            <p className={`mt-6 text-lg font-light opacity-70 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              Accédez aux documents officiels, livrets d'accueil et supports multimédias de HIS.
            </p>
          </div>

          {isLoggedIn && (
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/RessourcesForm")} 
              className="flex items-center gap-3 px-8 py-4 bg-blue-600 text-white font-black text-xs uppercase tracking-widest rounded-2xl hover:bg-blue-700 shadow-xl shadow-blue-600/30 transition-all"
            >
              <FaPlus /> Ajouter un document
            </motion.button>
          )}
        </div>

        {/* GRID STYLE */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {ressources.length > 0 ? (
            ressources.map((ressource, index) => (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                key={ressource.id || `res-${index}`}
                className={`group relative flex flex-col rounded-[2rem] border transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 ${
                  isDark ? "bg-gray-900/50 border-gray-800 hover:border-gray-700" : "bg-white border-gray-100 shadow-xl shadow-blue-900/5"
                }`}
              >
                {isLoggedIn && ressource.id && (
                  <button 
                    onClick={() => handleDelete(ressource.id, ressource.title)} 
                    className="absolute top-6 left-6 z-20 p-3 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all shadow-xl hover:scale-110"
                  >
                    <FaTrash size={14} />
                  </button>
                )}
                {renderResourceContent(ressource)}
              </motion.div>
            ))
          ) : (
            <div className="col-span-full text-center py-32 border-2 border-dashed rounded-[3rem] border-gray-200 dark:border-gray-800 opacity-50">
              <p className="text-xl font-light italic">Aucun document dans la bibliothèque actuellement.</p>
            </div>
          )}
        </div>
      </div>
    </Box>
  );
};

export default RessourcesList;